import React from "react";
import { X } from "lucide-react";

interface AppDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  maxWidth?: string;
  showCloseButton?: boolean;
  /**
   * Drop the drawer's own 16px inset and let the child own all padding.
   *
   * Off by default — every existing caller relies on the inset. Turn it on for
   * a drawer with a sticky header or footer whose divider has to run the full
   * width: inside the inset, a border stops 16px short of both edges and reads
   * as a floating line rather than a section break.
   */
  bleed?: boolean;
}

export const AppDrawer = ({
  isOpen,
  onClose,
  children,
  maxWidth = "30.25rem",
  showCloseButton = true,
  bleed = false,
}: AppDrawerProps) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-theme-bg-overlay transition-opacity"
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className="
          absolute right-0 top-0 h-full w-full 
          bg-theme-bg-surface 
          text-theme-text
          shadow-xl 
          transform transition-transform duration-300 ease-in-out 
          flex flex-col overflow-hidden
        "
        style={{ maxWidth }}
      >
        <div className={`flex flex-col h-full overflow-hidden ${bleed ? "" : "p-4"}`}>
          <div className="w-full relative flex flex-col flex-1 min-h-0 overflow-hidden">
            
            {showCloseButton && (
              <button
                onClick={onClose}
                className="
                  absolute top-2 right-2 p-2 
                  rounded-full 
                  transition-colors z-10
                  hover:bg-theme-bg-subtle
                "
              >
                <X size={18} className="text-theme-text-muted" />
              </button>
            )}

            {/* Content */}
            <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
              {children}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};